// this component will show the connection beetween the tables with connection type

import { BaseEdge, Edge, EdgeLabelRenderer, EdgeProps, getSmoothStepPath, useEdges } from '@xyflow/react';
import { IArrow } from '../../redux/_Dataset/types';

const getEdgeIndex = (
  edges: Edge[],
  currentEdgeId: string,
  handleId?: string | null,
): number => {
  if (!handleId || !currentEdgeId) return 0;

  const handlePrefix = handleId.split("-")[0];
  const handlePosition = handleId.split("-")[2]
  const connected = edges.filter((e) => {
    const sourcePrefix = e.sourceHandle?.split("-")[0];
    const sourcePosition = e.sourceHandle?.split("-")[2];
    const targetPrefix = e.targetHandle?.split("-")[0];
    const targetPosition = e.targetHandle?.split("-")[2];

    return (
      (sourcePrefix === handlePrefix && sourcePosition === handlePosition) ||
      (targetPrefix === handlePrefix && targetPosition === handlePosition)
    );
  });

  return connected.findIndex((e) => e.id === currentEdgeId);
};



function RelationshipEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  sourceHandleId,
  targetHandleId,
}: EdgeProps<Edge<{ arrow: IArrow; arrowHovered?: string }>>): JSX.Element {
  const edges = useEdges();

  const sourceIndex = getEdgeIndex(edges, id, sourceHandleId);
  const targetIndex = getEdgeIndex(edges, id, targetHandleId);
  
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 20, // adjust corner radius
  });

  // Extract cardinality from arrow data
  const arrow: IArrow | undefined = data?.arrow;
  const cardinality: string | undefined = arrow?.cardinality;

  // Parse cardinality to determine labels
  // Examples: "One to Many" -> "1" and "N"
  //           "Many to One" -> "N" and "1"
  //           "One to One" -> "1" and "1"
  //           "Many to Many" -> "N" and "N"
  const getCardinalityLabels = (card: string | undefined) => {
    if (!card) return { start: '', end: '' };

    const lower = card.toLowerCase();
    if (lower.includes('one to many')) return { start: '1', end: 'N' };
    if (lower.includes('many to one')) return { start: 'N', end: '1' };
    if (lower.includes('one to one')) return { start: '1', end: '1' };
    if (lower.includes('many to many')) return { start: 'N', end: 'N' };

    return { start: '', end: '' };
  };

  const { start: startLabel, end: endLabel } = getCardinalityLabels(cardinality);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeDasharray: 'none',
          zIndex: data?.arrowHovered === arrow?.arrowId ? 20 : 0,
        }}
      />
      <EdgeLabelRenderer>
        {/* Start label - positioned at source connection point */}
        {startLabel && (
          <div
            style={{
              position: 'absolute',
              left: sourcePosition === "right" ? 17 + sourceIndex * 5 : -17 - sourceIndex * 5,
              transform: `translate(-50%, -50%) translate(${sourceX}px,${sourceY}px)`,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: '600',
              color: '#374151',
              background: 'white',
              zIndex:
                data?.arrowHovered === arrow?.arrowId 
                  ? sourceIndex + 10
                  : sourceIndex,
              border:
                data?.arrowHovered === arrow?.arrowId
                  ? '1.5px solid #404040'
                  : '1.5px solid #9ca3af',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              pointerEvents: 'none',
            }}
          >
            {startLabel}
          </div>
        )}

        {/* End label - positioned at target connection point */}
        {endLabel && (
          <div
            style={{
              position: 'absolute',
              left: targetPosition === "left" ? -17 - targetIndex * 5 : 17 + targetIndex * 5,
              transform: `translate(-50%, -50%) translate(${targetX}px,${targetY}px)`,
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: '600',
              color: '#374151',
              background: 'white',
              zIndex:
                data?.arrowHovered === arrow?.arrowId ? targetIndex + 10 : targetIndex,
              border:
                data?.arrowHovered === arrow?.arrowId
                  ? '1.5px solid #404040'
                  : '1.5px solid #9ca3af',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              pointerEvents: 'none',
            }}
          >
            {endLabel}
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}

export default RelationshipEdge;
